import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { appstraxAuth, User } from '@appstrax/services/auth';
import { appstraxStorage } from '@appstrax/services/storage';

import { OrganizationProjectsService, ProjectService, ProjectUsersService, ToastService } from '@services';

import { Project, ProjectUsers } from '@models';
import { Organization, OrganizationProjects } from '@models';
import { OrganizationUsers, ProjectOrgRoles, ProjectUserRoles } from '@models';

import { WorkspaceFacade } from '@state';
import { applyLogoInputSelection } from '../../../utils/logo-file.util';

@Component({
  selector: 'app-create-project',
  templateUrl: './create-project.page.html',
  styleUrls: ['./create-project.page.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
})
export class CreateProjectPage implements OnInit {
  private projectUsersService = inject(ProjectUsersService);
  private orgProjectsService = inject(OrganizationProjectsService);
  private projectService = inject(ProjectService);
  project: Project = new Project();
  organizations: Organization[] = [];
  projectUser: ProjectUsers = new ProjectUsers();
  organization: Organization = new Organization();
  orgUser: OrganizationUsers = new OrganizationUsers();
  orgProject: OrganizationProjects = new OrganizationProjects();
  isOrgMenuOpen: boolean = false;

  errorMessage: string = '';

  isLoading: boolean = false;

  logoFile: File | null = null;
  logoPreviewUrl: string | null = null;
  backLink: string = '/create-organization';
  isEditMode: boolean = false;
  authStep: number | undefined;
  readonly setupSteps = [1, 2, 3];

  constructor(
    private store: WorkspaceFacade,
    private router: Router,
    private toast: ToastService,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    this.authStep = this.route.snapshot.data['authStep'] as number | undefined;
    const from = this.route.snapshot.queryParamMap.get('from');
    this.backLink = from === 'home' ? '/home' : '/create-organization';

    this.organizations = this.store.organizations();
    const preferredOrganizationId =
      this.route.snapshot.queryParamMap.get('organizationId') ??
      this.store.selectedOrganizationId();
    const preferredOrganization =
      preferredOrganizationId
        ? this.organizations.find((org) => org.id === preferredOrganizationId) ?? null
        : null;

    if (preferredOrganization) {
      this.organization = preferredOrganization;
    } else if (this.organizations.length === 1) {
      this.organization = this.organizations[0];
    }

    // Default: enable all project features for ease of use
    this.setAllFeatures(true);

    // Edit mode: prefill project and org if editId is present
    const editId = this.route.snapshot.queryParamMap.get('editId');
    if (editId) {
      this.isEditMode = true;
      try {
        // Prefer workspace state; fall back to fetch
        const existing = this.store.projects().find((p) => p.id === editId);
        this.project = existing ?? (await this.projectService.findById(editId));
        // Set preview to current logo if present
        this.logoPreviewUrl = this.project.logoUrl || null;
        // Resolve organization from orgProjects mapping
        const mapping = this.store.orgProjects().find((op) => op.projectId === this.project.id) ?? null;
        if (mapping) {
          this.orgProject = mapping;
          const org = this.store.organizations().find((o) => o.id === mapping.organizationId) ?? null;
          if (org) this.organization = org;
        }
      } catch (e: any) {
        this.toast.error('Failed to load project for editing', 'Error');
      }
    }
  }

  public onOrganizationChanged(organizationId: string) {
    const found = this.organizations.find((o) => o.id === organizationId);
    if (found) {
      this.organization = found;
    } else {
      this.organization = new Organization();
    }
  }

  public toggleOrgMenu(): void {
    this.isOrgMenuOpen = !this.isOrgMenuOpen;
  }

  public selectOrganization(org: Organization): void {
    this.organization = org;
    this.isOrgMenuOpen = false;
  }

  public setAllFeatures(enable: boolean) {
    type FeatureKey =
      | 'ticketManagement'
      | 'repoManagement'
      | 'documentationManagement'
      | 'complianceManagement'
      | 'devOpsManagement'
      | 'guardrailsManagement'
      | 'qualityManagement'
      | 'auditManagement'
      | 'projectStateManagement'
      | 'statsManagement';

    const featureKeys: FeatureKey[] = [
      'ticketManagement',
      'repoManagement',
      'documentationManagement',
      'complianceManagement',
      'devOpsManagement',
      'guardrailsManagement',
      'qualityManagement',
      'auditManagement',
      'projectStateManagement',
      'statsManagement',
    ];

    featureKeys.forEach((key) => (this.project[key] = enable));
  }

  async createProject(): Promise<void> {
    this.errorMessage = '';

    if (!this.isFormValid()) {
      this.errorMessage = 'Please enter all required fields';
      return;
    }

    this.isLoading = true;

    try {
      if (this.logoFile) await this.uploadProjectLogo(this.logoFile);

      if (this.isEditMode) {
        // Update existing project
        const originalOrgProject = this.store.orgProjects().find((op) => op.projectId === this.project.id) ?? null;
        this.project = await this.projectService.save(this.project);
        // Update org mapping if organization changed
        if (originalOrgProject && this.organization?.id && originalOrgProject.organizationId !== this.organization.id) {
          const updated = { ...originalOrgProject, organizationId: this.organization.id } as OrganizationProjects;
          await this.orgProjectsService.save(updated);
        } else if (!originalOrgProject && this.organization?.id) {
          const createdMapping = new OrganizationProjects();
          createdMapping.projectId = this.project.id;
          createdMapping.organizationId = this.organization.id;
          createdMapping.role = ProjectOrgRoles.PROVIDER;
          await this.orgProjectsService.save(createdMapping);
        }
        try {
          await this.store.refreshWorkspaceData();
        } catch {}
        this.toast.success('Project updated successfully', 'Success');
        this.router.navigate(['/home']);
      } else {
        const user: User = await appstraxAuth.getUser();
        let createdProjectId: string | null = null;
        let createdProjectUserId: string | null = null;

        try {
          this.project = await this.projectService.save(this.project);
          createdProjectId = this.project.id;

          this.projectUser.projectId = this.project.id;
          this.projectUser.userId = user.id;
          this.projectUser.role = ProjectUserRoles.ADMIN;

          this.projectUser = await this.projectUsersService.save(this.projectUser);
          createdProjectUserId = this.projectUser.id;

          this.orgProject.projectId = this.project.id;
          this.orgProject.organizationId = this.organization.id;
          this.orgProject.role = ProjectOrgRoles.PROVIDER;

          this.orgProject = await this.orgProjectsService.save(this.orgProject);
        } catch (error) {
          await this.rollbackCreatedProject(createdProjectId, createdProjectUserId);
          throw error;
        }

        if (this.project && this.projectUser && this.orgProject) {
          // Merge the new entities locally instead of reloading: a reload's
          // read-after-write can miss just-created records and wipe them.
          this.store.upsertEntities({
            projects: [this.project],
            projectUsers: [this.projectUser],
            orgProjects: [this.orgProject],
          });
          this.store.selectProject(this.project.id, this.organization.id);
          try {
            localStorage.setItem('repos.autoBootstrapProjectId', this.project.id);
          } catch {}
          this.toast.success('Project created successfully', 'Success');
          this.router.navigate(['/home']);
        }
      }
    } catch (error: any) {
      this.errorMessage = error.message;
      this.toast.error(error.message || (this.isEditMode ? 'Failed to update project' : 'Failed to create project'), 'Error');
    } finally {
      this.isLoading = false;
    }
  }

  public async uploadProjectLogo(file: File) {
    const response = await appstraxStorage.uploadFile(file, 'projectLogos');
    this.project.logoUrl = response.downloadUrl;
  }

  public onLogoSelected(event: Event) {
    applyLogoInputSelection(event, (file, previewUrl) => {
      this.logoFile = file;
      this.logoPreviewUrl = previewUrl;
    });
  }

  public isFormValid(): boolean {
    return (
      this.project.name != '' &&
      this.project.description != '' &&
      this.organization.id != ''
    );
  }

  private async rollbackCreatedProject(
    projectId: string | null,
    projectUserId: string | null,
  ): Promise<void> {
    const cleanupTasks: Promise<unknown>[] = [];

    if (projectUserId) {
      cleanupTasks.push(this.projectUsersService.delete(projectUserId).catch(() => undefined));
    }

    if (projectId) {
      cleanupTasks.push(this.projectService.delete(projectId).catch(() => undefined));
    }

    await Promise.all(cleanupTasks);
  }
}
